import { useAnnotatorVerificationTasks, useSubmitAnnotatorPretest } from '@/features/nectar/api'
import type { AudioAnnotationTaskDetail } from '@/features/nectar/api/models'
import { Button, Spinner } from '@/ui-kit'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui-kit/components/Dialog'
import { Trans, useLingui } from '@lingui/react/macro'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { getDefaultAnswer, validateAnswers } from '../audio-annotation/validateAnswers'
import { AnnotatorVerificationTaskItem } from './AnnotatorVerificationTaskItem'

type TaskAnswers = Record<string, unknown>

type Props = {
  open: boolean
  onClose: () => void
  projectId: string
}

export const AnnotatorVerificationDialog = ({ open, onClose, projectId }: Props) => {
  const { t } = useLingui()
  const {
    data: verificationTasks,
    isFetching,
    isError
  } = useAnnotatorVerificationTasks(projectId, 4, { enabled: open })
  const { mutateAsync: submit, isPending } = useSubmitAnnotatorPretest()

  const tasks = useMemo(() => (verificationTasks ?? []) as unknown as AudioAnnotationTaskDetail[], [verificationTasks])

  const [answers, setAnswers] = useState<Record<string, TaskAnswers>>({})
  const [currentStep, setCurrentStep] = useState(0)
  const [result, setResult] = useState<{ passed: boolean; correct: number; total: number } | null>(null)

  const isTaskComplete = (task: AudioAnnotationTaskDetail) =>
    validateAnswers(task.formSchema?.fields ?? [], answers[task.id] ?? {})

  const completedCount = tasks.filter(isTaskComplete).length
  const allAnswered = tasks.length > 0 && completedCount === tasks.length

  const currentTask = tasks[currentStep] as AudioAnnotationTaskDetail | undefined
  const currentAnswered = currentTask ? isTaskComplete(currentTask) : false
  const isLastStep = currentStep === tasks.length - 1

  // Seed default answers whenever a fresh set of tasks arrives.
  useEffect(() => {
    setAnswers(
      Object.fromEntries(
        tasks.map((task) => [
          task.id,
          Object.fromEntries((task.formSchema?.fields ?? []).map((field) => [field.key, getDefaultAnswer(field)]))
        ])
      )
    )
    setCurrentStep(0)
  }, [tasks])

  useEffect(() => {
    if (!open) {
      setAnswers({})
      setResult(null)
      setCurrentStep(0)
    }
  }, [open])

  const setTaskFieldValue = (taskId: string, key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [taskId]: { ...prev[taskId], [key]: value } }))
  }

  const handleClose = () => {
    if (isPending) return
    onClose()
  }

  const handleSubmit = async () => {
    if (!allAnswered || isPending) return
    try {
      const res = await submit({
        projectId,
        tasks: tasks.map((task) => ({
          taskId: task.id,
          fields: (task.formSchema?.fields ?? []).map((field) => ({
            fieldKey: field.key,
            answer: answers[task.id]?.[field.key]
          }))
        }))
      })
      if (res.status === 'PASSED') {
        toast.success(t`Verification passed`)
      } else {
        toast.error(t`Verification not passed.`)
      }
      onClose()
    } catch {
      toast.error(t`Failed to submit verification.`)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose()
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="max-mobile:top-0! max-mobile:left-0! max-mobile:h-dvh max-mobile:max-h-dvh! max-mobile:max-w-none! max-mobile:translate-x-0! max-mobile:translate-y-0! max-mobile:rounded-none max-mobile:p-4 max-mobile:pb-6 flex max-h-[85vh] max-w-[600px]! flex-col gap-6 overflow-hidden sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-[24px]/[32px]">
            <Trans>Annotator Verification</Trans>
          </DialogTitle>
        </DialogHeader>

        {!isFetching && !isError && !result && tasks.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="text-body-3 text-gray-80">
              <Trans>
                Task {currentStep + 1} of {tasks.length}
              </Trans>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className="bg-green h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${Math.round((completedCount / tasks.length) * 100)}%`,
                  boxShadow: completedCount > 0 ? '0 0 8px var(--color-green)' : 'none'
                }}
              />
            </div>
          </div>
        )}

        {isFetching ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : isError || tasks.length === 0 ? (
          <div className="flex flex-col gap-4">
            <div className="text-body-2 text-gray-80 my-4">
              <Trans>Verification is unavailable right now. Please try again later.</Trans>
            </div>

            <div className="ml-auto">
              <Button variant="bordered-gray" onClick={handleClose}>
                <Trans>Close</Trans>
              </Button>
            </div>
          </div>
        ) : result ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            {/* {result.passed ? (
              <CheckCircle className="text-green size-12" />
            ) : (
              <XCircle className="text-orange size-12" />
            )}
            <Text type="h5" className="text-gray-70">
              {result.passed ? <Trans>You passed!</Trans> : <Trans>Not quite</Trans>}
            </Text>
            <Text type="body2-regular" className="text-gray-80">
              <Trans>
                You answered {result.correct} of {result.total} correctly.
              </Trans>
            </Text>
            {result.passed ? (
              <Button className="mt-2 w-full" onClick={handleClose}>
                <Trans>Continue</Trans>
              </Button>
            ) : (
              <Button className="mt-2 w-full" onClick={handleRetry}>
                <Trans>Try again</Trans>
              </Button>
            )} */}
          </div>
        ) : currentTask ? (
          <div className="flex min-h-0 flex-1 flex-col gap-6">
            <div className="scrollbar h-[50svh] overflow-y-auto px-2 max-mobile:h-[70svh]!">
              <AnnotatorVerificationTaskItem
                key={currentTask.id}
                index={currentStep}
                task={currentTask}
                answers={answers[currentTask.id] ?? {}}
                onChange={(key, value) => setTaskFieldValue(currentTask.id, key, value)}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="bordered-gray"
                className="flex-1"
                onClick={() => (currentStep === 0 ? handleClose() : setCurrentStep((s) => s - 1))}
                isDisabled={isPending}
              >
                {currentStep === 0 ? <Trans>Cancel</Trans> : <Trans>Back</Trans>}
              </Button>
              {isLastStep ? (
                <Button className="flex-1" onClick={handleSubmit} isDisabled={!allAnswered || isPending}>
                  {isPending && <Spinner size="small" />}
                  <Trans>Submit</Trans>
                </Button>
              ) : (
                <Button
                  className="flex-1"
                  onClick={() => setCurrentStep((s) => s + 1)}
                  isDisabled={!currentAnswered || isPending}
                >
                  <Trans>Next</Trans>
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
